import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBriefcase,
  FaCalendarAlt,
  FaCalendarCheck,
  FaClock,
  FaCommentDots,
  FaHospital,
  FaMapMarkerAlt,
  FaSignOutAlt,
  FaUserCircle,
} from "react-icons/fa";

import PublicHeader from "../../components/PublicHeader";
import PublicFooter from "../../components/PublicFooter";
import PatientInbox from "../../components/PatientInbox";
import PatientClinicalHistory from "../../components/PatientClinicalHistory";
import { supabase } from "../../supabaseClient";

function formatDateTime(value) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Date to be confirmed";
  }

  return date.toLocaleString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getShiftDurationMinutes(shift) {
  const duration = Number(shift?.duration_minutes);
  return Number.isFinite(duration) && duration > 0 ? duration : 120;
}

function getShiftEndTime(shift) {
  const start = new Date(shift?.time).getTime();
  if (!Number.isFinite(start)) return null;
  return new Date(start + getShiftDurationMinutes(shift) * 60 * 1000);
}

function formatRoleplayNhsNumber(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);

  if (digits.length !== 10) {
    return digits || "Not assigned";
  }

  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

function optionalNumber(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function calculateAgeFromDob(value, onDate = new Date()) {
  if (!value) return null;

  const dob = new Date(`${value}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return null;

  let age = onDate.getFullYear() - dob.getFullYear();
  const monthDifference = onDate.getMonth() - dob.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && onDate.getDate() < dob.getDate())
  ) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

function todayIsoDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

async function getRobloxAvatar(username) {
  if (!username) return "";

  try {
    const response = await fetch("/api/robloxavatar", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });

    const result = await response.json().catch(() => ({}));
    return result?.imageUrl || "";
  } catch {
    return "";
  }
}

function checkInWindow(appointment) {
  if (!appointment?.appointment_datetime) {
    return false;
  }

  if (["Cancelled", "Completed"].includes(appointment.appointment_status)) {
    return false;
  }

  if (
    appointment.checked_in_at ||
    appointment.appointment_status === "Checked In"
  ) {
    return false;
  }

  const target = new Date(appointment.appointment_datetime).getTime();

  const now = Date.now();

  return now >= target - 60 * 60 * 1000 && now <= target + 30 * 60 * 1000;
}

function PatientDashboard({ session }) {
  const [account, setAccount] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [appointmentRequests, setAppointmentRequests] = useState([]);
  const [publicShifts, setPublicShifts] = useState([]);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [bookingDraft, setBookingDraft] = useState({
    department: "Outpatients",
    location: "Prince George University Hospital",
    datetime: "",
    reason: "",
  });
  const [requestWorkingId, setRequestWorkingId] = useState(null);
  const [requestMessage, setRequestMessage] = useState("");
  const [changeDrafts, setChangeDrafts] = useState({});

  const [complaintDraft, setComplaintDraft] = useState({
    anonymous: false,
    complained_about: "",
    complaint_text: "",
  });
  const [complaintSubmitting, setComplaintSubmitting] = useState(false);
  const [complaintMessage, setComplaintMessage] = useState("");
  const [complaintError, setComplaintError] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [workingId, setWorkingId] = useState(null);
  const [profileDraft, setProfileDraft] = useState({
    roleplay_name: "",
    date_of_birth: "",
    pronouns: "",
    height_cm: "",
    weight_kg: "",
    allergies: "",
    preferences: "",
    accessibility_needs: "",
    communication_preferences: "",
    discord_id: "",
    discord_username: "",
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [discordLookupLoading, setDiscordLookupLoading] = useState(false);
  const [discordLookupError, setDiscordLookupError] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [imageBusy, setImageBusy] = useState(false);

  const loadDashboard = useCallback(async () => {
    if (!session?.user?.id) {
      return;
    }

    setLoading(true);
    setError("");

    const accountResult = await supabase
      .from("patient_portal_accounts")
      .select(
        `
        id,
        roblox_user_id,
        roblox_username,
        roblox_display_name,
        roleplay_name,
        roleplay_nhs_number,
        date_of_birth,
        pronouns,
        height_cm,
        weight_kg,
        allergies,
        preferences,
        accessibility_needs,
        communication_preferences,
        discord_id,
        discord_username,
        profile_image_path,
        created_at
      `,
      )
      .eq("id", session.user.id)
      .maybeSingle();

    if (accountResult.error) {
      console.error(accountResult.error);

      setError("Your patient portal account could not be loaded.");

      setLoading(false);
      return;
    }

    if (!accountResult.data) {
      setAccount(null);
      setAppointments([]);
      setAttendances([]);
      setAppointmentRequests([]);
      setPublicShifts([]);
      setLoading(false);

      return;
    }

    const nextAccount = accountResult.data;
    setAccount(nextAccount);
    setProfileDraft({
      roleplay_name: nextAccount.roleplay_name || "",
      date_of_birth: nextAccount.date_of_birth || "",
      pronouns: nextAccount.pronouns || "",
      height_cm: nextAccount.height_cm ?? "",
      weight_kg: nextAccount.weight_kg ?? "",
      allergies: nextAccount.allergies || "",
      preferences: nextAccount.preferences || "",
      accessibility_needs: nextAccount.accessibility_needs || "",
      communication_preferences: nextAccount.communication_preferences || "",
      discord_id: nextAccount.discord_id || "",
      discord_username: nextAccount.discord_username || "",
    });

    if (nextAccount.profile_image_path) {
      const { data: signedImage } = await supabase.storage
        .from("patient-profile-images")
        .createSignedUrl(nextAccount.profile_image_path, 60 * 60);

      setProfileImageUrl(signedImage?.signedUrl || "");
    } else {
      setProfileImageUrl(await getRobloxAvatar(nextAccount.roblox_username));
    }

    const appointmentResult = await supabase
      .from("appointments")
      .select(
        `
        id,
        portal_account_id,
        patient_id,
        patient_full_name,
        roleplay_name,
        department,
        appointment_datetime,
        duration_minutes,
        location,
        address,
        preparation,
        appointment_status,
        status,
        checked_in_at,
        checked_in_method,
        queue_status,
        called_at,
        called_location
      `,
      )
      .eq("portal_account_id", session.user.id)
      .order("appointment_datetime", {
        ascending: true,
      });

    if (appointmentResult.error) {
      console.error(appointmentResult.error);

      setError("Your appointments could not be loaded.");

      setAppointments([]);
    } else {
      setAppointments(appointmentResult.data || []);
    }

    const requestResult = await supabase
      .from("appointment_requests")
      .select(
        `
        id,
        appointment_id,
        request_type,
        requested_department,
        requested_location,
        requested_datetime,
        requested_duration_minutes,
        patient_reason,
        status,
        created_at,
        reviewed_at,
        resulting_appointment_id
      `,
      )
      .eq("portal_account_id", session.user.id)
      .order("created_at", { ascending: false });

    if (requestResult.error) {
      console.error("Unable to load appointment requests:", requestResult.error);
      setAppointmentRequests([]);
    } else {
      setAppointmentRequests(requestResult.data || []);
    }

    const shiftWindowStart = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const shiftWindowEnd = new Date().toISOString();

    let shiftResult = await supabase
      .from("public_shift_calendar")
      .select("id, title, location, time, duration_minutes")
      .gte("time", shiftWindowStart)
      .lte("time", shiftWindowEnd)
      .order("time", { ascending: true });

    // Backwards-compatible fallback while the ongoing-shifts migration is
    // being applied. Older public_shift_calendar views do not expose duration.
    if (shiftResult.error && /duration_minutes/i.test(shiftResult.error.message || "")) {
      shiftResult = await supabase
        .from("public_shift_calendar")
        .select("id, title, location, time")
        .gte("time", shiftWindowStart)
        .lte("time", shiftWindowEnd)
        .order("time", { ascending: true });
    }

    if (shiftResult.error) {
      console.error("Unable to load ongoing shifts:", shiftResult.error);
      setPublicShifts([]);
    } else {
      setPublicShifts(shiftResult.data || []);
    }

    /*
     * Loads any current hospital attendance
     * belonging to the signed-in patient account.
     *
     * The database RPC keeps internal patient
     * records protected while returning only
     * information required by this dashboard.
     */
    const attendanceResult = await supabase.rpc(
      "get_my_current_ecamis_attendances",
    );

    if (attendanceResult.error) {
      console.error(
        "Unable to load current attendance:",
        attendanceResult.error,
      );

      setAttendances([]);
    } else {
      setAttendances(attendanceResult.data || []);
    }

    setLoading(false);
  }, [session?.user?.id]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const interval = window.setInterval(() => setClockNow(Date.now()), 30000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const discordId = String(profileDraft.discord_id || "").trim();

    setDiscordLookupError("");

    if (!discordId) {
      setDiscordLookupLoading(false);
      setProfileDraft((current) =>
        current.discord_username
          ? { ...current, discord_username: "" }
          : current,
      );
      return undefined;
    }

    if (!/^\d{17,20}$/.test(discordId)) {
      setDiscordLookupLoading(false);
      setProfileDraft((current) =>
        current.discord_username
          ? { ...current, discord_username: "" }
          : current,
      );
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setDiscordLookupLoading(true);

      try {
        const response = await fetch("/api/patientDiscord", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token || ""}`,
          },
          signal: controller.signal,
          body: JSON.stringify({
            discordId,
            persist: false,
          }),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(result?.error || "The Discord account could not be found.");
        }

        setProfileDraft((current) => {
          if (String(current.discord_id || "").trim() !== discordId) {
            return current;
          }

          return {
            ...current,
            discord_id: result.discordId || discordId,
            discord_username: result.username || "",
          };
        });
      } catch (lookupError) {
        if (lookupError?.name === "AbortError") return;

        setProfileDraft((current) =>
          String(current.discord_id || "").trim() === discordId
            ? { ...current, discord_username: "" }
            : current,
        );
        setDiscordLookupError(
          lookupError?.message || "The Discord account could not be found.",
        );
      } finally {
        if (!controller.signal.aborted) {
          setDiscordLookupLoading(false);
        }
      }
    }, 600);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [profileDraft.discord_id, session?.access_token]);

  async function checkIn(appointmentId) {
    setWorkingId(appointmentId);
    setError("");

    const { error: checkInError } = await supabase.rpc("patient_check_in", {
      target_appointment_id: appointmentId,
    });

    if (checkInError) {
      setError(checkInError.message || "You could not be checked in.");
    } else {
      await loadDashboard();
    }

    setWorkingId(null);
  }

  async function submitBookingRequest(event) {
    event.preventDefault();
    setRequestMessage("");
    setError("");

    if (!bookingDraft.datetime) {
      setError("Choose a preferred date and time for your appointment request.");
      return;
    }

    if (new Date(bookingDraft.datetime).getTime() <= Date.now()) {
      setError("Choose a future date and time for your appointment request.");
      return;
    }

    setRequestWorkingId("new-booking");

    const { error: requestError } = await supabase.rpc(
      "submit_my_appointment_request",
      {
        p_department: bookingDraft.department.trim() || "Outpatients",
        p_location: bookingDraft.location || "Prince George University Hospital",
        p_requested_datetime: bookingDraft.datetime,
        p_reason: bookingDraft.reason.trim() || null,
        p_duration_minutes: 30,
      },
    );

    if (requestError) {
      setError(requestError.message || "Your appointment request could not be submitted.");
    } else {
      setBookingDraft({
        department: "Outpatients",
        location: "Prince George University Hospital",
        datetime: "",
        reason: "",
      });
      setRequestMessage("Your appointment request has been sent to RUHT for review.");
      await loadDashboard();
    }

    setRequestWorkingId(null);
  }

  function startReschedule(appointment) {
    const currentValue = appointment?.appointment_datetime
      ? new Date(appointment.appointment_datetime).toISOString().slice(0, 16)
      : "";

    setChangeDrafts((current) => ({
      ...current,
      [appointment.id]: {
        open: true,
        location: appointment.location || "Prince George University Hospital",
        datetime: currentValue,
        reason: "",
      },
    }));
  }

  async function submitRescheduleRequest(appointment) {
    const draft = changeDrafts[appointment.id] || {};
    setRequestMessage("");
    setError("");

    if (!draft.datetime) {
      setError("Choose the date and time you would prefer.");
      return;
    }

    if (new Date(draft.datetime).getTime() <= Date.now()) {
      setError("Choose a future date and time for your reschedule request.");
      return;
    }

    setRequestWorkingId(`reschedule-${appointment.id}`);

    const { error: requestError } = await supabase.rpc(
      "request_my_appointment_change",
      {
        target_appointment_id: appointment.id,
        p_request_type: "reschedule",
        p_requested_datetime: draft.datetime,
        p_requested_location:
          draft.location || appointment.location || "Prince George University Hospital",
        p_reason: String(draft.reason || "").trim() || null,
      },
    );

    if (requestError) {
      setError(requestError.message || "Your reschedule request could not be submitted.");
    } else {
      setChangeDrafts((current) => ({
        ...current,
        [appointment.id]: { open: false, location: "", datetime: "", reason: "" },
      }));
      setRequestMessage("Your reschedule request has been sent to RUHT for review.");
      await loadDashboard();
    }

    setRequestWorkingId(null);
  }

  async function submitCancellationRequest(appointment) {
    const alreadyPending = appointmentRequests.some(
      (request) =>
        request.appointment_id === appointment.id &&
        request.request_type === "cancellation" &&
        request.status === "Pending",
    );

    if (alreadyPending) {
      setError("A cancellation request is already pending for this appointment.");
      return;
    }

    if (!window.confirm("Send a request to cancel this appointment?")) return;

    setRequestWorkingId(`cancel-${appointment.id}`);
    setRequestMessage("");
    setError("");

    const { error: requestError } = await supabase.rpc(
      "request_my_appointment_change",
      {
        target_appointment_id: appointment.id,
        p_request_type: "cancellation",
        p_requested_datetime: null,
        p_requested_location: appointment.location || null,
        p_reason: null,
      },
    );

    if (requestError) {
      setError(requestError.message || "Your cancellation request could not be submitted.");
    } else {
      setRequestMessage("Your cancellation request has been sent to RUHT for review.");
      await loadDashboard();
    }

    setRequestWorkingId(null);
  }

  async function saveProfile(event) {
    event.preventDefault();
    setProfileSaving(true);
    setError("");

    const discordId = String(profileDraft.discord_id || "").trim();

    if (discordId && !/^\d{17,20}$/.test(discordId)) {
      setError("Enter a valid Discord user ID before saving your details.");
      setProfileSaving(false);
      return;
    }

    if (discordLookupLoading) {
      setError("Wait for the Discord account lookup to finish before saving.");
      setProfileSaving(false);
      return;
    }

    if (discordId && !profileDraft.discord_username) {
      setError("The Discord user ID must resolve to an account before it can be saved.");
      setProfileSaving(false);
      return;
    }

    const { data, error: saveError } = await supabase.rpc(
      "update_my_patient_portal_profile",
      {
        p_roleplay_name: profileDraft.roleplay_name.trim(),
        p_date_of_birth: profileDraft.date_of_birth || null,
        p_pronouns: profileDraft.pronouns.trim(),
        p_height_cm: optionalNumber(profileDraft.height_cm),
        p_weight_kg: optionalNumber(profileDraft.weight_kg),
        p_allergies: profileDraft.allergies.trim(),
        p_preferences: profileDraft.preferences.trim(),
        p_accessibility_needs: profileDraft.accessibility_needs.trim(),
        p_communication_preferences:
          profileDraft.communication_preferences.trim(),
      },
    );

    if (saveError) {
      setError(saveError.message || "Your details could not be saved.");
      setProfileSaving(false);
      return;
    }

    try {
      const discordResponse = await fetch("/api/patientDiscord", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || ""}`,
        },
        body: JSON.stringify({
          discordId,
          persist: true,
        }),
      });

      const discordResult = await discordResponse.json().catch(() => ({}));

      if (!discordResponse.ok) {
        throw new Error(
          discordResult?.error || "Your Discord details could not be saved.",
        );
      }

      const updated = Array.isArray(data) ? data[0] : data;
      const nextAccount = updated
        ? {
            ...updated,
            discord_id: discordResult.discordId || null,
            discord_username: discordResult.username || null,
          }
        : null;

      if (nextAccount) setAccount(nextAccount);

      setProfileDraft((current) => ({
        ...current,
        discord_id: discordResult.discordId || "",
        discord_username: discordResult.username || "",
      }));
    } catch (discordSaveError) {
      setError(
        discordSaveError?.message || "Your Discord details could not be saved.",
      );
    }

    setProfileSaving(false);
  }

  async function uploadProfileImage(event) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setError("Choose a PNG, JPEG or WebP image.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Profile pictures must be 5 MB or smaller.");
      return;
    }

    setImageBusy(true);
    setError("");

    const extension =
      file.type === "image/png"
        ? "png"
        : file.type === "image/webp"
          ? "webp"
          : "jpg";

    const newPath = `${session.user.id}/profile-${Date.now()}.${extension}`;
    const previousPath = account?.profile_image_path || null;

    const { error: uploadError } = await supabase.storage
      .from("patient-profile-images")
      .upload(newPath, file, {
        cacheControl: "3600",
        contentType: file.type,
        upsert: false,
      });

    if (uploadError) {
      setError(uploadError.message || "Your profile picture could not be uploaded.");
      setImageBusy(false);
      return;
    }

    const { error: imageSaveError } = await supabase.rpc(
      "set_my_patient_profile_image",
      { p_profile_image_path: newPath },
    );

    if (imageSaveError) {
      await supabase.storage.from("patient-profile-images").remove([newPath]);
      setError(imageSaveError.message || "Your profile picture could not be saved.");
      setImageBusy(false);
      return;
    }

    if (previousPath && previousPath !== newPath) {
      await supabase.storage
        .from("patient-profile-images")
        .remove([previousPath]);
    }

    const { data: signedImage } = await supabase.storage
      .from("patient-profile-images")
      .createSignedUrl(newPath, 60 * 60);

    setProfileImageUrl(signedImage?.signedUrl || "");
    setAccount((current) =>
      current ? { ...current, profile_image_path: newPath } : current,
    );
    setImageBusy(false);
  }

  async function useRobloxAvatar() {
    setImageBusy(true);
    setError("");

    const previousPath = account?.profile_image_path || null;
    const { error: clearError } = await supabase.rpc(
      "set_my_patient_profile_image",
      { p_profile_image_path: null },
    );

    if (clearError) {
      setError(clearError.message || "Your profile picture could not be changed.");
      setImageBusy(false);
      return;
    }

    if (previousPath) {
      await supabase.storage
        .from("patient-profile-images")
        .remove([previousPath]);
    }

    setProfileImageUrl(await getRobloxAvatar(account?.roblox_username));
    setAccount((current) =>
      current ? { ...current, profile_image_path: null } : current,
    );
    setImageBusy(false);
  }

  async function submitComplaint(event) {
    event.preventDefault();

    const complainedAbout = complaintDraft.complained_about.trim();
    const complaintText = complaintDraft.complaint_text.trim();

    setComplaintMessage("");
    setComplaintError("");

    if (!complaintText) {
      setComplaintError("Tell us what happened before submitting your complaint.");
      return;
    }

    setComplaintSubmitting(true);

    const reporterName = complaintDraft.anonymous
      ? null
      : account?.roleplay_name ||
        account?.roblox_display_name ||
        account?.roblox_username ||
        "Patient portal user";

    const { error: complaintSubmitError } = await supabase
      .from("complaints")
      .insert([
        {
          reporter_name: reporterName,
          complained_about: complainedAbout || null,
          complaint_text: complaintText,
          submitted_at: new Date().toISOString(),
          portal_account_id: account?.id || null,
          status: "Submitted",
        },
      ]);

    if (complaintSubmitError) {
      console.error("Unable to submit patient complaint:", complaintSubmitError);
      setComplaintError(
        complaintSubmitError.message ||
          "Your complaint could not be submitted. Please try again.",
      );
      setComplaintSubmitting(false);
      return;
    }

    setComplaintDraft({
      anonymous: false,
      complained_about: "",
      complaint_text: "",
    });
    setComplaintMessage(
      "Your complaint has been submitted to RUHT management for review.",
    );
    setComplaintSubmitting(false);
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  const ongoingShifts = useMemo(
    () =>
      publicShifts.filter((shift) => {
        const start = new Date(shift.time).getTime();
        const end = getShiftEndTime(shift)?.getTime();
        return Number.isFinite(start) && Number.isFinite(end) && start <= clockNow && clockNow < end;
      }),
    [clockNow, publicShifts],
  );

  const upcoming = useMemo(
    () =>
      appointments.filter(
        (appointment) =>
          new Date(appointment.appointment_datetime).getTime() >=
            Date.now() - 30 * 60 * 1000 &&
          appointment.status !== false &&
          String(appointment.appointment_status || "").toLowerCase() !== "cancelled",
      ),
    [appointments],
  );

  const past = useMemo(
    () =>
      appointments
        .filter(
          (appointment) => !upcoming.some((item) => item.id === appointment.id),
        )
        .slice(-8)
        .reverse(),
    [appointments, upcoming],
  );

  const pendingRequests = useMemo(
    () => appointmentRequests.filter((request) => request.status === "Pending"),
    [appointmentRequests],
  );

  const pendingRequestForAppointment = (appointmentId, type = null) =>
    pendingRequests.find(
      (request) =>
        request.appointment_id === appointmentId &&
        (!type || request.request_type === type),
    );

  if (loading) {
    return (
      <div className="pguh-patient-loading">Loading your patient portal…</div>
    );
  }

  if (!account) {
    return (
      <div className="pguh-public-content-card">
        <h1>Patient portal account not found</h1>

        <p>We could not find a patient portal account linked to this login.</p>

        <div className="pguh-patient-account-actions">
          <button className="nhsuk-button" type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="pguh-patient-dashboard-hero">
        <div>
          <span className="pguh-public-kicker">Patient portal</span>

          <h1>
            Welcome, {account.roleplay_name || account.roblox_display_name}
          </h1>

          <p>
            View your appointments, check in online, browse public shifts,
            manage your details and submit complaints to RUHT.
          </p>
        </div>

        <div className="pguh-patient-dashboard-hero__identity">
          <FaUserCircle aria-hidden="true" />

          <span>
            <strong>{account.roblox_username}</strong>

            <small>Roblox-linked patient account</small>
          </span>

          <button type="button" onClick={signOut}>
            <FaSignOutAlt aria-hidden="true" /> Sign out
          </button>
        </div>
      </section>

      {error && (
        <div className="pguh-public-error" role="alert">
          {error}
        </div>
      )}

      {requestMessage && (
        <div className="pguh-patient-request-message" role="status">
          {requestMessage}
        </div>
      )}

      <section className="pguh-patient-summary-grid">
        <div>
          <span>Upcoming appointments</span>

          <strong>{upcoming.length}</strong>
        </div>

        <div>
          <span>Currently checked in</span>

          <strong>{attendances.length}</strong>
        </div>

        <div>
          <span>Pending requests</span>

          <strong>{pendingRequests.length}</strong>
        </div>

        <div>
          <span>Ongoing shifts</span>

          <strong>{ongoingShifts.length}</strong>
        </div>
      </section>

      <section className="pguh-public-content-card pguh-patient-ongoing-shifts">
        <div className="pguh-public-section-card__header">
          <div>
            <span className="pguh-public-kicker">Live hospital activity</span>
            <h2>Ongoing shifts</h2>
          </div>
          <Link to="/shifts">View hospital calendar</Link>
        </div>

        {ongoingShifts.length === 0 ? (
          <div className="pguh-patient-ongoing-shifts__empty">
            <FaClock aria-hidden="true" />
            <div>
              <strong>No shifts are currently ongoing</strong>
              <span>Active RUHT shifts will appear here automatically.</span>
            </div>
          </div>
        ) : (
          <div className="pguh-patient-ongoing-shifts__grid">
            {ongoingShifts.map((shift) => (
              <article key={shift.id} className="pguh-patient-ongoing-shift-card">
                <span className="pguh-patient-ongoing-shift-card__status">Ongoing now</span>
                <h3>{shift.title || "Hospital shift"}</h3>
                <div>
                  <FaMapMarkerAlt aria-hidden="true" />
                  <span>{shift.location || "Hospital site"}</span>
                </div>
                <div>
                  <FaClock aria-hidden="true" />
                  <span>
                    {formatDateTime(shift.time)} – {formatDateTime(getShiftEndTime(shift))}
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {attendances.length > 0 && (
        <section className="pguh-public-content-card pguh-patient-current-attendance">
          <div className="pguh-public-section-card__header">
            <div>
              <span className="pguh-public-kicker">Your visit</span>

              <h2>Current hospital attendance</h2>
            </div>
          </div>

          <div className="pguh-patient-current-attendance__body">
            <FaHospital aria-hidden="true" />

            <div>
              <strong>You are currently checked in</strong>

              <span>
                {attendances[0]?.arrival_time
                  ? `Checked in ${formatDateTime(attendances[0].arrival_time)}`
                  : "Currently checked in"}
              </span>

              {attendances[0]?.location && (
                <small>{attendances[0].location}</small>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="pguh-public-content-card pguh-patient-profile-card">
        <div className="pguh-public-section-card__header">
          <div>
            <span className="pguh-public-kicker">Your details</span>
            <h2>Patient profile</h2>
          </div>
        </div>

        <div className="pguh-patient-profile-card__layout">
          <div className="pguh-patient-profile-picture">
            <div className="pguh-patient-profile-picture__frame">
              {profileImageUrl ? (
                <img src={profileImageUrl} alt="Your patient profile" />
              ) : (
                <FaUserCircle aria-hidden="true" />
              )}
            </div>

            <label className="nhsuk-button nhsuk-button--secondary pguh-patient-image-button">
              {imageBusy ? "Uploading…" : "Choose profile picture"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={uploadProfileImage}
                disabled={imageBusy}
              />
            </label>

            {account.profile_image_path && (
              <button
                type="button"
                className="pguh-text-button"
                onClick={useRobloxAvatar}
                disabled={imageBusy}
              >
                Use Roblox avatar instead
              </button>
            )}

            <p className="nhsuk-hint">
              Use a roleplay or Roblox image. Avoid uploading a real-world
              personal photograph.
            </p>
          </div>

          <form className="pguh-patient-profile-form" onSubmit={saveProfile}>
            <div className="nhsuk-form-group pguh-patient-nhs-number-field">
              <label className="nhsuk-label" htmlFor="patient-roleplay-nhs-number">
                Roleplay NHS number
              </label>
              <input
                id="patient-roleplay-nhs-number"
                className="nhsuk-input"
                value={formatRoleplayNhsNumber(account.roleplay_nhs_number)}
                readOnly
              />
              <div className="nhsuk-hint">
                Your unique 10-digit RUHT roleplay identifier. This is not a
                real NHS number.
              </div>
            </div>

            <div className="pguh-patient-profile-form__two-column">
              <div className="nhsuk-form-group">
                <label className="nhsuk-label" htmlFor="patient-profile-name">
                  Roleplay name
                </label>
                <input
                  id="patient-profile-name"
                  className="nhsuk-input"
                  value={profileDraft.roleplay_name}
                  onChange={(event) =>
                    setProfileDraft((current) => ({
                      ...current,
                      roleplay_name: event.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div className="nhsuk-form-group">
                <label className="nhsuk-label" htmlFor="patient-date-of-birth">
                  Date of birth
                </label>
                <input
                  id="patient-date-of-birth"
                  type="date"
                  className="nhsuk-input"
                  value={profileDraft.date_of_birth}
                  max={todayIsoDate()}
                  onChange={(event) =>
                    setProfileDraft((current) => ({
                      ...current,
                      date_of_birth: event.target.value,
                    }))
                  }
                  required
                />
                <div className="nhsuk-hint">
                  Your current roleplay age is {calculateAgeFromDob(profileDraft.date_of_birth) ?? "not available"}. This is used to fill your age when an appointment is created.
                </div>
              </div>

              <div className="nhsuk-form-group">
                <label className="nhsuk-label" htmlFor="patient-pronouns">
                  Pronouns (optional)
                </label>
                <input
                  id="patient-pronouns"
                  className="nhsuk-input"
                  value={profileDraft.pronouns}
                  onChange={(event) =>
                    setProfileDraft((current) => ({
                      ...current,
                      pronouns: event.target.value,
                    }))
                  }
                  placeholder="e.g. he/him"
                />
              </div>

              <div className="nhsuk-form-group">
                <label className="nhsuk-label" htmlFor="patient-discord-id">
                  Discord user ID (optional)
                </label>
                <input
                  id="patient-discord-id"
                  className="nhsuk-input"
                  inputMode="numeric"
                  autoComplete="off"
                  value={profileDraft.discord_id}
                  onChange={(event) =>
                    setProfileDraft((current) => ({
                      ...current,
                      discord_id: event.target.value.replace(/\D/g, "").slice(0, 20),
                      discord_username: "",
                    }))
                  }
                  placeholder="e.g. 123456789012345678"
                />
                <div className="nhsuk-hint">
                  Enter your Discord user ID. Your username is looked up automatically through the RUHT bot.
                </div>
                {discordLookupError && (
                  <div className="nhsuk-error-message" role="alert">
                    {discordLookupError}
                  </div>
                )}
              </div>

              <div className="nhsuk-form-group">
                <label className="nhsuk-label" htmlFor="patient-discord-username">
                  Discord username
                </label>
                <input
                  id="patient-discord-username"
                  className="nhsuk-input"
                  value={
                    discordLookupLoading
                      ? "Looking up Discord account…"
                      : profileDraft.discord_username
                  }
                  placeholder="Automatically filled from Discord"
                  readOnly
                />
                <div className="nhsuk-hint">
                  This field cannot be edited manually. Change the Discord user ID above to update it.
                </div>
              </div>

              <div className="nhsuk-form-group">
                <label className="nhsuk-label" htmlFor="patient-height">
                  Height (cm)
                </label>
                <input
                  id="patient-height"
                  type="number"
                  min="50"
                  max="250"
                  step="0.1"
                  className="nhsuk-input"
                  value={profileDraft.height_cm}
                  onChange={(event) =>
                    setProfileDraft((current) => ({
                      ...current,
                      height_cm: event.target.value,
                    }))
                  }
                />
              </div>

              <div className="nhsuk-form-group">
                <label className="nhsuk-label" htmlFor="patient-weight">
                  Weight (kg)
                </label>
                <input
                  id="patient-weight"
                  type="number"
                  min="10"
                  max="500"
                  step="0.1"
                  className="nhsuk-input"
                  value={profileDraft.weight_kg}
                  onChange={(event) =>
                    setProfileDraft((current) => ({
                      ...current,
                      weight_kg: event.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="nhsuk-form-group">
              <label className="nhsuk-label" htmlFor="patient-allergies">
                Allergies
              </label>
              <textarea
                id="patient-allergies"
                className="nhsuk-textarea"
                rows="3"
                value={profileDraft.allergies}
                onChange={(event) =>
                  setProfileDraft((current) => ({
                    ...current,
                    allergies: event.target.value,
                  }))
                }
                placeholder="Roleplay allergies or 'None known'"
              />
            </div>

            <div className="nhsuk-form-group">
              <label className="nhsuk-label" htmlFor="patient-accessibility">
                Accessibility needs
              </label>
              <textarea
                id="patient-accessibility"
                className="nhsuk-textarea"
                rows="3"
                value={profileDraft.accessibility_needs}
                onChange={(event) =>
                  setProfileDraft((current) => ({
                    ...current,
                    accessibility_needs: event.target.value,
                  }))
                }
                placeholder="Any roleplay accessibility requirements"
              />
            </div>

            <div className="nhsuk-form-group">
              <label className="nhsuk-label" htmlFor="patient-communication">
                Communication preferences
              </label>
              <textarea
                id="patient-communication"
                className="nhsuk-textarea"
                rows="3"
                value={profileDraft.communication_preferences}
                onChange={(event) =>
                  setProfileDraft((current) => ({
                    ...current,
                    communication_preferences: event.target.value,
                  }))
                }
                placeholder="How you prefer communication during roleplay"
              />
            </div>

            <div className="nhsuk-form-group">
              <label className="nhsuk-label" htmlFor="patient-preferences">
                Other preferences
              </label>
              <textarea
                id="patient-preferences"
                className="nhsuk-textarea"
                rows="3"
                value={profileDraft.preferences}
                onChange={(event) =>
                  setProfileDraft((current) => ({
                    ...current,
                    preferences: event.target.value,
                  }))
                }
                placeholder="Any other roleplay preferences you want RUHT to know"
              />
            </div>

            <button
              type="submit"
              className="nhsuk-button"
              disabled={profileSaving}
            >
              {profileSaving ? "Saving…" : "Save my details"}
            </button>
          </form>
        </div>
      </section>

      <PatientInbox session={session} />

      <PatientClinicalHistory session={session} account={account} />

      <section
        className="pguh-patient-portal-links"
        aria-label="Patient portal links"
      >
        <Link to="/shifts" className="pguh-public-section-card">
          <div className="pguh-public-section-card__icon">
            <FaCalendarAlt aria-hidden="true" />
          </div>

          <div>
            <span className="pguh-public-kicker">RUHT calendar</span>

            <h2>View public shifts</h2>

            <p>See upcoming public sessions and hospital service times.</p>
          </div>
        </Link>

        <Link to="/vacancies" className="pguh-public-section-card">
          <div className="pguh-public-section-card__icon">
            <FaBriefcase aria-hidden="true" />
          </div>

          <div>
            <span className="pguh-public-kicker">Recruitment</span>

            <h2>Browse vacancies</h2>

            <p>View current RUHT vacancies and submit an application online.</p>
          </div>
        </Link>

        <a href="#patient-inbox" className="pguh-public-section-card">
          <div className="pguh-public-section-card__icon">
            <FaCommentDots aria-hidden="true" />
          </div>

          <div>
            <span className="pguh-public-kicker">Messages</span>
            <h2>Patient inbox</h2>
            <p>Read appointment calls, care updates, results notices and visit summaries.</p>
          </div>
        </a>

        <a href="#patient-history" className="pguh-public-section-card">
          <div className="pguh-public-section-card__icon">
            <FaCommentDots aria-hidden="true" />
          </div>
          <div>
            <span className="pguh-public-kicker">Your record</span>
            <h2>Medical history & documents</h2>
            <p>View shared results, visit summaries, documents and complaint progress.</p>
          </div>
        </a>

        <a href="#patient-complaint" className="pguh-public-section-card">
          <div className="pguh-public-section-card__icon">
            <FaCommentDots aria-hidden="true" />
          </div>

          <div>
            <span className="pguh-public-kicker">Complaints</span>

            <h2>Make a complaint</h2>

            <p>Send a complaint directly to the RUHT management team.</p>
          </div>
        </a>
      </section>

      <section
        id="patient-complaint"
        className="pguh-public-content-card pguh-patient-complaint-card"
      >
        <div className="pguh-public-section-card__header">
          <div>
            <span className="pguh-public-kicker">Complaints</span>
            <h2>Make a complaint</h2>
            <p>
              Tell RUHT management about an issue with a person, department,
              hospital service or your roleplay care.
            </p>
          </div>
        </div>

        <div className="pguh-patient-complaint-intro">
          <FaCommentDots aria-hidden="true" />
          <div>
            <strong>Your complaint goes to the existing RUHT complaints inbox.</strong>
            <span>
              You can submit using your patient portal roleplay name, or choose
              to remain anonymous.
            </span>
          </div>
        </div>

        {complaintMessage && (
          <div className="pguh-patient-complaint-message" role="status">
            {complaintMessage}
          </div>
        )}

        {complaintError && (
          <div className="pguh-public-error" role="alert">
            {complaintError}
          </div>
        )}

        <form className="pguh-patient-complaint-form" onSubmit={submitComplaint}>
          <div className="pguh-patient-complaint-identity">
            <div>
              <span>Submitting as</span>
              <strong>
                {complaintDraft.anonymous
                  ? "Anonymous"
                  : account.roleplay_name ||
                    account.roblox_display_name ||
                    account.roblox_username}
              </strong>
              {!complaintDraft.anonymous && (
                <small>
                  Roleplay NHS number: {formatRoleplayNhsNumber(account.roleplay_nhs_number)}
                </small>
              )}
            </div>

            <label className="pguh-patient-complaint-anonymous">
              <input
                id="patient-complaint-anonymous"
                type="checkbox"
                checked={complaintDraft.anonymous}
                onChange={(event) =>
                  setComplaintDraft((current) => ({
                    ...current,
                    anonymous: event.target.checked,
                  }))
                }
              />
              <span>Submit anonymously</span>
            </label>
          </div>

          <div className="nhsuk-form-group">
            <label className="nhsuk-label" htmlFor="patient-complaint-about">
              Who or what is your complaint about?
            </label>
            <div className="nhsuk-hint">
              For example, a staff member, department, hospital location or service.
            </div>
            <input
              id="patient-complaint-about"
              name="patient_complaint_about"
              className="nhsuk-input"
              type="text"
              value={complaintDraft.complained_about}
              onChange={(event) =>
                setComplaintDraft((current) => ({
                  ...current,
                  complained_about: event.target.value,
                }))
              }
              placeholder="Optional"
            />
          </div>

          <div className="nhsuk-form-group">
            <label className="nhsuk-label" htmlFor="patient-complaint-details">
              What is your complaint?
            </label>
            <div className="nhsuk-hint">
              Describe what happened and include any roleplay details that may help
              RUHT management review the complaint.
            </div>
            <textarea
              id="patient-complaint-details"
              name="patient_complaint_details"
              className="nhsuk-textarea"
              rows="7"
              value={complaintDraft.complaint_text}
              onChange={(event) =>
                setComplaintDraft((current) => ({
                  ...current,
                  complaint_text: event.target.value,
                }))
              }
              required
            />
          </div>

          <div className="pguh-public-info-strip pguh-patient-complaint-notice">
            This is a roleplay complaints service. Do not submit real-world medical
            information, emergency reports or sensitive real-world personal data.
          </div>

          <button
            className="nhsuk-button"
            type="submit"
            disabled={complaintSubmitting}
          >
            {complaintSubmitting ? "Submitting complaint…" : "Submit complaint"}
          </button>
        </form>
      </section>

      <section className="pguh-public-content-card pguh-patient-booking-card">
        <div className="pguh-public-section-card__header">
          <div>
            <span className="pguh-public-kicker">Appointments</span>
            <h2>Request an appointment</h2>
            <p>Choose a preferred date and time. RUHT staff will review your request and assign a clinician.</p>
          </div>
        </div>

        <form className="pguh-patient-booking-form" onSubmit={submitBookingRequest}>
          <div className="nhsuk-form-group">
            <label className="nhsuk-label" htmlFor="patient-booking-department">
              Department
            </label>
            <input
              id="patient-booking-department"
              className="nhsuk-input"
              type="text"
              value={bookingDraft.department}
              onChange={(event) =>
                setBookingDraft((current) => ({ ...current, department: event.target.value }))
              }
              required
            />
          </div>

          <div className="nhsuk-form-group">
            <label className="nhsuk-label" htmlFor="patient-booking-location">
              Hospital
            </label>
            <select
              id="patient-booking-location"
              className="nhsuk-select"
              value={bookingDraft.location}
              onChange={(event) =>
                setBookingDraft((current) => ({ ...current, location: event.target.value }))
              }
              required
            >
              <option value="Prince George University Hospital">
                Prince George University Hospital
              </option>
              <option value="Duke of York Hospital">Duke of York Hospital</option>
            </select>
          </div>

          <div className="nhsuk-form-group">
            <label className="nhsuk-label" htmlFor="patient-booking-datetime">
              Preferred date and time
            </label>
            <input
              id="patient-booking-datetime"
              className="nhsuk-input"
              type="datetime-local"
              value={bookingDraft.datetime}
              onChange={(event) =>
                setBookingDraft((current) => ({ ...current, datetime: event.target.value }))
              }
              required
            />
            <div className="nhsuk-hint">This is a request until RUHT confirms the appointment.</div>
          </div>

          <div className="nhsuk-form-group pguh-patient-booking-form__full">
            <label className="nhsuk-label" htmlFor="patient-booking-reason">
              Reason or information for RUHT (optional)
            </label>
            <textarea
              id="patient-booking-reason"
              className="nhsuk-textarea"
              rows="3"
              value={bookingDraft.reason}
              onChange={(event) =>
                setBookingDraft((current) => ({ ...current, reason: event.target.value }))
              }
            />
          </div>

          <div className="pguh-patient-booking-form__full">
            <button
              className="nhsuk-button"
              type="submit"
              disabled={requestWorkingId === "new-booking"}
            >
              {requestWorkingId === "new-booking" ? "Sending request…" : "Request appointment"}
            </button>
          </div>
        </form>

        {appointmentRequests.length > 0 && (
          <div className="pguh-patient-request-list">
            <h3>Recent requests</h3>
            {appointmentRequests.slice(0, 6).map((request) => (
              <div key={request.id} className="pguh-patient-request-row">
                <div>
                  <strong>
                    {request.request_type === "new_booking"
                      ? "New appointment"
                      : request.request_type === "reschedule"
                        ? "Reschedule"
                        : "Cancellation"}
                  </strong>
                  <span>
                    {request.request_type === "cancellation"
                      ? "Cancellation requested"
                      : `${formatDateTime(request.requested_datetime)} · ${
                          request.requested_location || "Prince George University Hospital"
                        }`}
                  </span>
                </div>
                <small className={`pguh-patient-request-status pguh-patient-request-status--${String(request.status || "pending").toLowerCase()}`}>
                  {request.status || "Pending"}
                </small>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="pguh-public-content-card pguh-patient-appointments">
        <div className="pguh-public-section-card__header">
          <div>
            <span className="pguh-public-kicker">Your appointments</span>

            <h2>Upcoming appointments</h2>
          </div>
        </div>

        {upcoming.length === 0 ? (
          <div className="pguh-public-empty">
            <FaCalendarCheck aria-hidden="true" />

            <p>You do not have any upcoming appointments.</p>
          </div>
        ) : (
          <div className="pguh-patient-appointment-list">
            {upcoming.map((appointment) => {
              const canCheckIn = checkInWindow(appointment);

              const isCheckedIn =
                Boolean(appointment.checked_in_at) ||
                appointment.appointment_status === "Checked In";

              const pendingReschedule = pendingRequestForAppointment(
                appointment.id,
                "reschedule",
              );
              const pendingCancellation = pendingRequestForAppointment(
                appointment.id,
                "cancellation",
              );
              const changeDraft = changeDrafts[appointment.id] || {};

              return (
                <article
                  key={appointment.id}
                  className={`pguh-patient-appointment${
                    isCheckedIn ? " pguh-patient-appointment--checked-in" : ""
                  }`}
                >
                  <div className="pguh-patient-appointment__main">
                    <span className="pguh-patient-status">
                      {appointment.appointment_status || "Scheduled"}
                    </span>

                    <h3>{appointment.department || "Hospital appointment"}</h3>

                    <div className="pguh-patient-appointment__meta">
                      <span>
                        <FaClock aria-hidden="true" />{" "}
                        {formatDateTime(appointment.appointment_datetime)}
                      </span>

                      <span>
                        <FaMapMarkerAlt aria-hidden="true" />{" "}
                        {appointment.location ||
                          "Prince George University Hospital"}
                      </span>
                    </div>

                    {appointment.preparation && (
                      <p>
                        <strong>Preparation:</strong> {appointment.preparation}
                      </p>
                    )}

                    {appointment.address && (
                      <p>
                        <strong>Location details:</strong> {appointment.address}
                      </p>
                    )}
                  </div>

                  <div className="pguh-patient-appointment__action">
                    {isCheckedIn ? (
                      <div className={`pguh-patient-checked-in${appointment.queue_status === "Called" ? " pguh-patient-checked-in--called" : ""}`}>
                        <FaCalendarCheck aria-hidden="true" />
                        <strong>{appointment.queue_status === "Called" ? "You have been called" : "Checked in"}</strong>
                        {appointment.queue_status === "Called" && appointment.called_location ? (
                          <span className="pguh-patient-call-location">Please proceed to {appointment.called_location}</span>
                        ) : (
                          <small>
                            {appointment.checked_in_at
                              ? formatDateTime(appointment.checked_in_at)
                              : "Checked in"}
                          </small>
                        )}
                      </div>
                    ) : (
                      <>
                        {canCheckIn ? (
                          <button
                            className="nhsuk-button"
                            type="button"
                            disabled={workingId === appointment.id}
                            onClick={() => checkIn(appointment.id)}
                          >
                            {workingId === appointment.id ? "Checking in…" : "Check in now"}
                          </button>
                        ) : (
                          <div className="pguh-patient-checkin-wait">
                            <strong>Check-in unavailable</strong>
                            <small>Check-in opens 60 minutes before your appointment.</small>
                          </div>
                        )}

                        <div className="pguh-patient-appointment__manage">
                          {pendingReschedule ? (
                            <div className="pguh-patient-request-pending">Reschedule request pending</div>
                          ) : (
                            <button
                              className="nhsuk-button nhsuk-button--secondary"
                              type="button"
                              onClick={() => startReschedule(appointment)}
                            >
                              Request reschedule
                            </button>
                          )}

                          {pendingCancellation ? (
                            <div className="pguh-patient-request-pending pguh-patient-request-pending--cancel">Cancellation request pending</div>
                          ) : (
                            <button
                              className="pguh-text-button pguh-patient-cancel-request"
                              type="button"
                              disabled={requestWorkingId === `cancel-${appointment.id}`}
                              onClick={() => submitCancellationRequest(appointment)}
                            >
                              {requestWorkingId === `cancel-${appointment.id}`
                                ? "Sending cancellation request…"
                                : "Request cancellation"}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {changeDraft.open && !pendingReschedule && (
                    <div className="pguh-patient-reschedule-form">
                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label" htmlFor={`reschedule-location-${appointment.id}`}>
                          Preferred hospital
                        </label>
                        <select
                          id={`reschedule-location-${appointment.id}`}
                          className="nhsuk-select"
                          value={
                            changeDraft.location ||
                            appointment.location ||
                            "Prince George University Hospital"
                          }
                          onChange={(event) =>
                            setChangeDrafts((current) => ({
                              ...current,
                              [appointment.id]: {
                                ...(current[appointment.id] || {}),
                                open: true,
                                location: event.target.value,
                              },
                            }))
                          }
                        >
                          <option value="Prince George University Hospital">
                            Prince George University Hospital
                          </option>
                          <option value="Duke of York Hospital">Duke of York Hospital</option>
                        </select>
                      </div>

                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label" htmlFor={`reschedule-${appointment.id}`}>
                          Preferred new date and time
                        </label>
                        <input
                          id={`reschedule-${appointment.id}`}
                          className="nhsuk-input"
                          type="datetime-local"
                          value={changeDraft.datetime || ""}
                          onChange={(event) =>
                            setChangeDrafts((current) => ({
                              ...current,
                              [appointment.id]: {
                                ...(current[appointment.id] || {}),
                                open: true,
                                datetime: event.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label" htmlFor={`reschedule-reason-${appointment.id}`}>
                          Reason (optional)
                        </label>
                        <input
                          id={`reschedule-reason-${appointment.id}`}
                          className="nhsuk-input"
                          type="text"
                          value={changeDraft.reason || ""}
                          onChange={(event) =>
                            setChangeDrafts((current) => ({
                              ...current,
                              [appointment.id]: {
                                ...(current[appointment.id] || {}),
                                open: true,
                                reason: event.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="pguh-patient-reschedule-form__actions">
                        <button
                          className="nhsuk-button"
                          type="button"
                          disabled={requestWorkingId === `reschedule-${appointment.id}`}
                          onClick={() => submitRescheduleRequest(appointment)}
                        >
                          {requestWorkingId === `reschedule-${appointment.id}`
                            ? "Sending request…"
                            : "Send reschedule request"}
                        </button>
                        <button
                          className="nhsuk-button nhsuk-button--secondary"
                          type="button"
                          onClick={() =>
                            setChangeDrafts((current) => ({
                              ...current,
                              [appointment.id]: { open: false, datetime: "", reason: "" },
                            }))
                          }
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>

      {past.length > 0 && (
        <section className="pguh-public-content-card pguh-patient-history">
          <h2>Recent and cancelled appointments</h2>

          <div className="pguh-patient-history__list">
            {past.map((appointment) => {
              const isCancelled =
                appointment.status === false ||
                String(appointment.appointment_status || "").toLowerCase() === "cancelled";

              return (
                <div
                  key={appointment.id}
                  className={isCancelled ? "pguh-patient-history__cancelled" : ""}
                >
                  <span>{formatDateTime(appointment.appointment_datetime)}</span>
                  <strong>{appointment.department || "Hospital appointment"}</strong>
                  <small>{isCancelled ? "Cancelled" : appointment.appointment_status || "Completed"}</small>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </>
  );
}

export default function PatientPortal({ session, profile }) {
  const [mode, setMode] = useState("login");

  const [robloxName, setRobloxName] = useState("");

  const [roleplayName, setRoleplayName] = useState("");

  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  async function handleLogin(event) {
    event.preventDefault();

    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/patientAuthLookup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          robloxName: robloxName.trim(),
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result.error || "The patient portal account could not be found.",
        );
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: result.email,
        password,
      });

      if (signInError) {
        throw new Error("The Roblox username or password is incorrect.");
      }
    } catch (loginError) {
      setError(loginError.message || "You could not be signed in.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSignup(event) {
    event.preventDefault();

    setLoading(true);
    setError("");

    try {
      if (password.length < 6) {
        throw new Error("Your password must contain at least 6 characters.");
      }

      const response = await fetch("/api/signupPatientRoblox", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          robloxName: robloxName.trim(),

          roleplayName: roleplayName.trim(),

          password,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result.error || "The patient portal account could not be created.",
        );
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: result.email,
        password,
      });

      if (signInError) {
        throw new Error(
          "Your account was created, but automatic sign-in failed. Try signing in with your Roblox username.",
        );
      }
    } catch (signupError) {
      setError(
        signupError.message ||
          "The patient portal account could not be created.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="pguh-public-page-shell">
      <PublicHeader session={session} profile={profile} />

      <main
        id="main-content"
        tabIndex="-1"
        className="pguh-public-standard-page pguh-patient-portal-page"
      >
        <div className="nhsuk-width-container">
          {session ? (
            <PatientDashboard session={session} />
          ) : (
            <div className="pguh-patient-auth-layout">
              <section className="pguh-patient-auth-intro">
                <span className="pguh-public-kicker">
                  RUHT patient services
                </span>

                <h1>Patient portal</h1>

                <p>
                  Create a Roblox-linked patient portal account to view your
                  appointments, check in online, browse shifts, submit complaints
                  and apply for vacancies.
                </p>

                <div className="pguh-patient-auth-feature">
                  <FaCalendarCheck aria-hidden="true" />

                  <span>
                    <strong>Online check-in</strong>

                    <small>
                      Check in for eligible appointments directly through the
                      patient portal.
                    </small>
                  </span>
                </div>

                <div className="pguh-patient-auth-feature">
                  <FaCalendarAlt aria-hidden="true" />

                  <span>
                    <strong>Appointments and services</strong>

                    <small>
                      Keep track of your upcoming appointments and view public
                      RUHT service times.
                    </small>
                  </span>
                </div>

                <div className="pguh-patient-auth-feature">
                  <FaBriefcase aria-hidden="true" />

                  <span>
                    <strong>Vacancies</strong>

                    <small>
                      Browse current RUHT vacancies and apply online.
                    </small>
                  </span>
                </div>

                <div className="pguh-patient-auth-feature">
                  <FaCommentDots aria-hidden="true" />

                  <span>
                    <strong>Complaints</strong>

                    <small>
                      Submit a roleplay complaint directly to RUHT management.
                    </small>
                  </span>
                </div>

                <p className="pguh-public-info-strip">
                  Only enter roleplay information requested by RUHT. Do not use
                  this service for real-world medical emergencies or real
                  clinical information.
                </p>
              </section>

              <section className="pguh-patient-auth-card">
                <div className="pguh-patient-auth-tabs">
                  <button
                    type="button"
                    className={mode === "login" ? "is-active" : ""}
                    onClick={() => {
                      setMode("login");
                      setError("");
                    }}
                  >
                    Sign in
                  </button>

                  <button
                    type="button"
                    className={mode === "signup" ? "is-active" : ""}
                    onClick={() => {
                      setMode("signup");
                      setError("");
                    }}
                  >
                    Create account
                  </button>
                </div>

                <h2>
                  {mode === "login"
                    ? "Sign in with Roblox"
                    : "Create a patient portal account"}
                </h2>

                {error && (
                  <div className="pguh-public-error" role="alert">
                    {error}
                  </div>
                )}

                <form onSubmit={mode === "login" ? handleLogin : handleSignup}>
                  <div className="nhsuk-form-group">
                    <label
                      className="nhsuk-label"
                      htmlFor="patient-roblox-name"
                    >
                      Roblox username
                    </label>

                    <div className="nhsuk-hint">
                      Enter your Roblox username to access your RUHT patient
                      portal account.
                    </div>

                    <input
                      id="patient-roblox-name"
                      className="nhsuk-input"
                      value={robloxName}
                      onChange={(event) => setRobloxName(event.target.value)}
                      autoComplete="username"
                      required
                    />
                  </div>

                  {mode === "signup" && (
                    <div className="nhsuk-form-group">
                      <label
                        className="nhsuk-label"
                        htmlFor="patient-roleplay-name"
                      >
                        Roleplay name
                      </label>

                      <input
                        id="patient-roleplay-name"
                        className="nhsuk-input"
                        value={roleplayName}
                        onChange={(event) =>
                          setRoleplayName(event.target.value)
                        }
                        required
                      />
                    </div>
                  )}

                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="patient-password">
                      Password
                    </label>

                    <input
                      id="patient-password"
                      type="password"
                      className="nhsuk-input"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete={
                        mode === "login" ? "current-password" : "new-password"
                      }
                      required
                      minLength="6"
                    />
                  </div>

                  <button
                    className="nhsuk-button pguh-full-button"
                    type="submit"
                    disabled={loading}
                  >
                    {loading
                      ? "Please wait…"
                      : mode === "login"
                        ? "Sign in to patient portal"
                        : "Create patient portal account"}
                  </button>
                </form>
              </section>
            </div>
          )}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
